/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';

import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';
import { Steps } from 'primereact/steps';
import { confirmDialog } from 'primereact/confirmdialog';

import ContactForm from './components/ContactForm/ContactForm';
import CaseInformation from './components/CaseInformation/CaseInformation';
import FeeAndPayments from './components/FeePayment/FeePayment';

import { totalCountEvent } from 'services/generalServices';
import { useLeadContext } from '../Context/LeadContext';
import { getCustomFields } from 'services/Settings';
import { createContact, updateContactDetails, contactDetails } from 'services/contact';
import { closeLead, createLead, updateLeadDetails } from 'services/lead/leadServices';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { prepareBillingData } from '../helpers/helpers';
import { mergeValueField, removeUnderScoreFields, setAppTitle, stripCountryCode } from 'utils/utils';
import useDynamicHeight from 'components/UI/NavbarHeight/useDynamicHeight';
import { generateFullName } from 'utils/generateFullNameUtils';
import { addRelation, updateRelation } from 'services/relationServices';

const AddNewLead = ({ handleClose, selectedLead }) => {
  const { handleSubmit, ...formProps } = useForm({
    defaultValues: {
      ...selectedLead?.draft_payload,
      contact: {
        ...selectedLead?.draft_payload?.contact,
        phone: stripCountryCode(selectedLead?.draft_payload?.contact?.phone),
        custom_field: [],
      },
      case_description: {
        ...selectedLead?.draft_payload?.case_description,
        custom_field: [],
      },
    },
  });

  const isFormChanged = formProps?.formState?.isDirty;
  const contactData = formProps?.getValues()?.contact;
  const practice_area = formProps?.getValues()?.case_description?.case_practice_area;

  const { addToast } = useToast();

  const {
    paymentStructures,
    leadStages,
    practiceAreaList,
    caseMatterList,
    leadSourceList,
    officeLocationList,
    courtList,
    loadCourtContacts,
    loadLeadSources,
    businessRole,
    relationRole,
    loadTotalCount,
    venueOptions,
    loadVenue,
    loadLeadReferrals,
    leadReferralsList,
    practiceArea,
    setPracticeArea,
  } = useLeadContext();

  const [activeIndex, setActiveIndex] = useState(0);
  const [customFields, setCustomFields] = useState([]);
  const [currentTabIndex, setCurrentTabIndex] = useState(selectedLead?.draft_payload?.currentTabIndex || 0);
  const [selectedContact, setSelectedContact] = useState(selectedLead?.draft_payload?.selectedContact || {});
  const [duplicateContact, setDuplicateContact] = useState(selectedLead?.draft_payload?.duplicateContact || '');
  const [conflictCheckResult, setConflictCheckResult] = useState({});
  const [loading, setLoading] = useState(false);

  const validationErrors = formProps.formState.errors;
  const hasErrorOncontacts = validationErrors?.contact || validationErrors?.relations || validationErrors?.client;
  const { navHeight } = useDynamicHeight();

  // Dynamic height for lead-add-container
  const headerRef = useRef(null);
  const stepsRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState('auto');

  useLayoutEffect(() => {
    function updateHeight() {
      setTimeout(() => {
        const headerH = headerRef.current ? headerRef.current.offsetHeight : 0;
        const stepsH = stepsRef.current ? stepsRef.current.offsetHeight : 0;
        const navH = navHeight || 0;
        const availableHeight = window.innerHeight - navH - headerH - stepsH - 38;
        setContainerHeight(`${Math.max(availableHeight, 0)}px`);
      }, 0);
    }
    updateHeight();
    const ro = new window.ResizeObserver(updateHeight);
    if (headerRef.current) ro.observe(headerRef.current);
    if (stepsRef.current) ro.observe(stepsRef.current);
    const sidebar = document.querySelector('.lead-sidebar');
    if (sidebar) ro.observe(sidebar);
    window.addEventListener('resize', updateHeight);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  const tabs = [
    { label: 'Contacts', className: hasErrorOncontacts ? 'lead-step-validation' : '' },
    { label: 'Information', className: validationErrors?.online_lead_fields ? 'lead-step-validation' : '' },
    { label: 'Billing preferences', className: validationErrors?.billing_preferences ? 'lead-step-validation' : '' },
  ];

  useEffect(() => {
    const loadCustomFields = async () => {
      try {
        const response = await totalCountEvent('custom-field');
        const res = await getCustomFields('', response.data);
        setCustomFields(res.data?.['custom-field']);
      } catch (err) {
        console.error(err);
      }
    };
    loadCustomFields();
  }, []);

  useEffect(() => {
    setPracticeArea(practice_area);
  }, [practice_area]);

  //To set Default value for office Location field
  useEffect(() => {
    if (officeLocationList?.length > 0) {
      const officeLoc = officeLocationList?.find((data) => data.is_primary_office === 'true')?.office_location_id;
      const selectedOfficeLocation = selectedLead?.draft_payload?.case_description?.office_location;
      const currentOffice = formProps.watch('case_description.office_location');
      if (currentOffice && selectedOfficeLocation) {
        const locationId = officeLocationList?.find((data) => data?.office_name === selectedOfficeLocation)?.office_location_id;
        formProps.setValue('case_description.office_location', locationId);
      } else if (!currentOffice && !selectedOfficeLocation) {
        formProps.setValue('case_description.office_location', officeLoc);
      }
    }
  }, [officeLocationList]);

  //To set Default value for lead stage field
  useEffect(() => {
    if (leadStages?.length > 0) {
      const newStageSk = leadStages?.find((data) => data.lead_stage_name?.toLowerCase() === 'new')?.sk;
      formProps.setValue('leadStageSk', newStageSk);
    }
  }, [leadStages]);

  const onNextClick = () => {
    if (activeIndex === 2) {
      setLoading(true);
      handleSubmit(createLeadHandle)().finally(() => {
        setLoading(false);
      });
    } else {
      setActiveIndex((preVal) => preVal + 1);
    }
  };

  const createLeadHandle = async (data) => {
    setLoading(true);
    try {
      let newContacts = [];
      let oldContacts = [];
      if (data?.contact?.first_name && !!data?.contact?.isOld === false) {
        newContacts.push(
          new Promise((resolve, reject) => {
            createContact({ ...data?.contact, contact_type: 'person' }, true)
              .then((response) => {
                resolve({
                  ...response.data,
                  ...data?.contact,
                  type: data?.client?.first_name ? 'on-behalf' : 'lead',
                  contact_type: 'person',
                });
              })
              .catch((error) => reject(error));
          })
        );
      } else if (data?.contact?.isOld) {
        oldContacts.push({ ...data?.contact, type: data?.client?.first_name ? 'on-behalf' : 'lead' });
      }
      if (data?.client?.first_name && !!data?.client?.isOld === false) {
        newContacts.push(
          new Promise((resolve, reject) => {
            createContact({ ...data?.client, contact_type: 'person' }, true)
              .then((response) => {
                resolve({
                  ...response.data,
                  ...data?.client,
                  type: 'lead',
                  contact_type: 'person',
                });
              })
              .catch((error) => {
                reject(error);
              });
          })
        );
      } else if (data?.client?.isOld) {
        oldContacts.push({ ...data?.client, type: 'lead' });
      }
      const contacts = await Promise.all(newContacts);

      if (oldContacts.length > 0) {
        oldContacts.forEach((contact) => {
          if (contact?.role) {
            delete contact?.role;
          }
        });
      }

      const contactsWithoutRole = [...(data?.relations || []), ...contacts, ...oldContacts].filter((contact) => !contact?.role);
      const clientId = contactsWithoutRole.length > 0 ? contactsWithoutRole[0].contact_id : null;
      const _contacts = [...(data?.relations || []), ...contacts, ...oldContacts].map((contact) => {
        if (contact?.role) {
          return { ...contact, client_id: clientId };
        } else {
          return contact;
        }
      });

      const leadDetails = { ...data?.lead_details };
      const leadSourceId = leadSourceList?.find((item) => item?.source?.toLowerCase() === 'referral')?.lead_source_id;
      if (leadDetails.source !== leadSourceId) {
        leadDetails.gratitude = null;
        leadDetails.gratitude_item = '';
      }
      const newStageSk = leadStages?.find((data) => data.lead_stage_name?.toLowerCase() === 'new')?.sk;

      // Find custom rate for the assigned team member
      let lead_assigned_to_with_rate = [];
      if (data?.lead_assigned_to) {
        const customRate = data?.billing_preferences?.custom_rates?.find(
          (rate) => rate?.team_member?.contact_id === data?.lead_assigned_to?.contact_id
        );
        lead_assigned_to_with_rate = [
          {
            ...data.lead_assigned_to,
            ...(customRate?.rate && { custom_rate: customRate.rate }),
          },
        ];
      }

      const formData = {
        lead_stage: data?.leadStageSk || newStageSk,
        case_description: {
          ...data?.case_description,
          potential_case_value: data?.case_description?.potential_case_value?.toString(),
          case_matter: data?.case_description?.case_matter || '',
        },
        lead_details: { ...leadDetails, tags: leadDetails.tags ?? [] },
        contact: _contacts,
        lead_assigned_to: lead_assigned_to_with_rate,
        billing_preferences: prepareBillingData(data?.billing_preferences),
        referred_from: leadDetails.source !== leadSourceId ? null : data?.referred_from,
        draft_pk: selectedLead?.pk || undefined,
        draft_sk: selectedLead?.sk || undefined,
        online_lead_fields: data?.online_lead_fields,
      };
      if (_contacts.find((val) => val.is_billing_contact)) {
        formData.billing_contact = _contacts.find((val) => val.is_billing_contact).sk;
      }

      const updateOldContact = async (contactData) => {
        const existRelations = await contactDetails(contactData.sk, contactData.pk, contactData.contact_id);
        const existingIds = existRelations?.data?.relations?.map((c) => c.contact_id) || [];
        const newRelations = data?.relations?.filter((item) => !existingIds.includes(item?.contact_id));

        await updateContactDetails({ ...contactData, relations: [...(data?.relations || [])] }, true)
          .then(async () => {
            if (data?.relations?.length !== 0) {
              await updateRelation({ relations: data?.relations }).catch((error) => {
                console.log(error);
              });
            }

            if (newRelations?.length > 0) {
              await addRelation(contactData.contact_id, { relations: newRelations }, true).catch((error) => {
                console.log(error);
              });
            }

            addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.LEAD_UPDATE_SUCCESS);
          })
          .catch((error) => {
            addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.LEAD_UPDATE_FAILURE);
            console.error(error);
          });
      };

      if (data?.contact?.isOld) {
        await updateOldContact(data.contact);
      }
      if (data?.client?.isOld) {
        await updateOldContact(data.client);
      }
      await createLead(removeUnderScoreFields(formData))
        .then(() => {
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.LEAD_CREATE_SUCCESS);
          loadTotalCount();
          handleClose('created');
        })
        .catch((error) => {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.LEAD_CREATE_FAILURE);
          console.error(error);
        })
        .finally(() => setLoading(false));
    } catch (error) {
      console.log(error, 'error');
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.LEAD_CREATE_FAILURE);
      setLoading(false);
    }
  };

  const onPrevClick = () => setActiveIndex((preVal) => preVal - 1);

  const handleSaveDraft = async () => {
    const newStageSk = leadStages?.find((data) => data.lead_stage_name?.toLowerCase() === 'new')?.sk;
    const formData = formProps.getValues();
    const leadDetails = { ...formData?.lead_details };
    const leadSourceId = leadSourceList?.find((item) => item?.source?.toLowerCase() === 'referral')?.lead_source_id;
    if (leadDetails.source !== leadSourceId) {
      leadDetails.gratitude = null;
      leadDetails.gratitude_item = '';
      delete leadDetails._source_;
    }
    const payload = {
      draft_payload: {
        ...formData,
        lead_details: { ...leadDetails },
        referred_from: leadDetails.source !== leadSourceId ? null : formData?.referred_from,
        currentTabIndex,
        selectedContact,
        duplicateContact,
        draft_payload: undefined,
        _practice_area_: practiceAreaList?.find((data) => data?.practice_area_id === formData?.case_description?.case_practice_area)
          ?.practice_area_name,
      },
      is_draft: true,
      lead_stage: newStageSk,
    };
    if (selectedLead?.pk && selectedLead?.sk) {
      updateLeadDetails(selectedLead?.sk, payload)
        .then(() => {
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.LEAD_DRAFT_UPDATE_SUCCESS);
          handleClose('created');
        })
        .catch((error) => {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.LEAD_DRAFT_UPDATE_FAILURE);
          console.error(error);
        });
    } else {
      createLead(payload)
        .then(() => {
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.LEAD_DRAFT_CREATE_SUCCESS);
          loadTotalCount();
          handleClose('created');
        })
        .catch((error) => {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.LEAD_DRAFT_CREATE_FAILURE);
          console.error(error);
        });
    }
  };

  const discardLead = () => {
    confirmDialog({
      message: 'Are you sure you want to discard this lead?',
      header: 'Confirmation',
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      accept: () => {
        if (selectedLead?.pk && selectedLead?.sk) {
          closeLead(selectedLead?.sk, { draft_sk: selectedLead?.sk, draft_pk: selectedLead?.pk })
            .then(() => {
              addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.LEAD_DISCARD_SUCCESS);
              handleClose('created');
            })
            .catch((error) => {
              console.error(error);
              addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.LEAD_CREATE_FAILURE);
            });
        } else {
          handleClose();
        }
      },
      reject: () => null,
    });
  };

  const handleBack = () => {
    if (isFormChanged) {
      confirmDialog({
        message: 'Warning: Any unsaved data will be lost if you proceed. Are you sure you want to continue?',
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          handleClose();
        },
        reject: () => null,
        closable: false,
      });
    } else {
      handleClose();
    }
  };

  useEffect(() => {
    if (customFields) {
      formProps.setValue(
        'contact.custom_field',
        mergeValueField(
          customFields.filter((v) => v.module === 'contact'),
          selectedLead?.draft_payload?.contact?.custom_field || []
        )
      );
      formProps.setValue(
        'case_description.custom_field',
        mergeValueField(
          customFields.filter((v) => v.module === 'case'),
          selectedLead?.draft_payload?.case_description?.custom_field || []
        )
      );
    }
  }, [customFields]);

  useEffect(() => {
    const isDraft = selectedLead?.is_draft;
    const contact = selectedLead?.draft_payload?.contact || {};
    const leadName = isDraft ? generateFullName(contact) : '';

    setAppTitle('Leads', leadName);

    // clean up function
    return () => {
      setAppTitle();
    };
  }, [selectedLead]);

  return (
    <Sidebar
      visible
      position="right"
      fullScreen
      header={null}
      onHide={handleClose}
      showCloseIcon={false}
      className="lead-sidebar"
      style={{ top: `${navHeight}px`, height: `calc(100vh - ${navHeight}px)` }}
    >
      <div ref={headerRef} className="d-flex justify-content-between align-items-center my-3 flex-wrap">
        <div className="d-flex align-items-center justify-content-start">
          {/* <div className="icon-box me-2 me-md-4 pointer" onClick={handleClose}> */}
          <div
            className="icon-box me-2 me-md-4 pointer"
            onClick={() => {
              handleBack();
            }}
          >
            <i className="icon-back" />
          </div>
          <h6 className="sub-title mb-0">Add New Lead</h6>
        </div>
        {/* top buttons */}
        <div className="d-flex justify-content-end add-lead-buttons flex-wrap gap-2">
          <Button type="button" className="p-button p-button-secondary" onClick={discardLead}>
            Discard Draft
          </Button>
          <Button type="button" className="p-button p-button-secondary" onClick={handleSaveDraft}>
            Save as Draft
          </Button>
          <Button type="button" disabled={activeIndex === 0} className="p-button p-button-secondary" onClick={onPrevClick}>
            Previous Step
          </Button>
          <Button type="button" className="p-button p-button-secondary" disabled={loading} onClick={onNextClick}>
            {activeIndex && activeIndex === 2 ? 'Add Lead' : 'Next Step'}
          </Button>
        </div>
      </div>

      <div className="case-sidebar addnew-lead pe-lg-0 me-lg-0">
        <div ref={stepsRef}>
          <Steps model={tabs} activeIndex={activeIndex} onSelect={(e) => setActiveIndex(e.index)} className="cases-steps" />
        </div>
        <div className="container-fluid px-0 mx-0 lead-add-container overflow-y-auto overflow-x-hidden" style={{ height: containerHeight }}>
          {activeIndex !== 0 && (
            <div className="row my-4">
              <div className="col">
                <h5 className="caption-bold">Client information</h5>
                <div className="card-bg client-data-box p-3 mb-3">
                  <div className="d-flex flex-column flex-md-row gap-md-4 gap-2">
                    <div
                      className="info-item w-fit-content d-md-flex flex-column flex-md-row align-items-start align-items-md-center"
                      style={{
                        width: '100%',
                        ...(typeof window !== 'undefined' && window.innerWidth >= 768 ? { width: 'fit-content' } : {}),
                      }}
                    >
                      <span className="small d-block p-medium me-0 me-md-2 mb-1 mb-md-0">Name:</span>
                      <span className="client-info-bold">
                        {currentTabIndex === 1
                          ? formProps?.getValues('client.first_name') || formProps?.getValues('client.last_name')
                            ? `${formProps?.getValues('client.first_name') || ''} ${formProps?.getValues('client.middle_name') || ''} ${
                                formProps?.getValues('client.last_name') || ''
                              }`.trim()
                            : '-'
                          : contactData?.first_name || contactData?.last_name
                          ? `${contactData?.first_name || ''} ${contactData?.middle_name || ''} ${contactData?.last_name || ''}`.trim()
                          : '-'}
                      </span>
                    </div>
                    <div
                      className="info-item w-fit-content d-md-flex flex-column flex-md-row align-items-start align-items-md-center"
                      style={{
                        width: '100%',
                        ...(typeof window !== 'undefined' && window.innerWidth >= 768 ? { width: 'fit-content' } : {}),
                      }}
                    >
                      <span className="small d-block p-medium me-0 me-md-2 mb-1 mb-md-0">Phone:</span>
                      <span className="client-info-bold">
                        {currentTabIndex === 1 ? formProps?.getValues('client.phone') || '-' : contactData?.phone || '-'}
                      </span>
                    </div>
                    <div
                      className="info-item w-fit-content d-md-flex flex-column flex-md-row align-items-start align-items-md-center"
                      style={{
                        width: '100%',
                        ...(typeof window !== 'undefined' && window.innerWidth >= 768 ? { width: 'fit-content' } : {}),
                      }}
                    >
                      <span className="small d-block p-medium me-0 me-md-2 mb-1 mb-md-0">Email:</span>
                      <span className="client-info-bold">
                        {currentTabIndex === 1 ? formProps?.getValues('client.email') || '-' : contactData?.email || '-'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className={activeIndex === 0 ? '' : 'd-none'}>
            <ContactForm
              {...{ handleSubmit, businessRole, relationRole, customFields }}
              {...{ currentTabIndex, setCurrentTabIndex, selectedContact, setSelectedContact, duplicateContact, setDuplicateContact }}
              {...formProps}
            />
          </div>
          <div className={activeIndex === 1 ? '' : 'd-none'}>
            <CaseInformation
              {...formProps}
              {...{
                handleSubmit,
                leadStages,
                loadLeadSources,
                leadSourceList,
                customFields,
                practiceAreaList,
                caseMatterList,
                officeLocationList,
                courtList,
                venueOptions,
                loadCourtContacts,
                loadVenue,
                conflictCheckResult,
                setConflictCheckResult,
                loadLeadReferrals,
                leadReferralsList,
                practiceArea,
              }}
            />
          </div>
          <div className={activeIndex === 2 ? '' : 'd-none'}>
            <FeeAndPayments {...{ paymentStructures, practiceAreaList }} {...formProps} />
          </div>
        </div>
      </div>

      {/* footer button
      {activeIndex !== 2 && (
        <div className="footer-btn">
          <Button type="button" className="p-button p-button-secondary me-2" onClick={discardLead}>
            Discard Draft
          </Button>
          <Button type="button" className="p-button p-button-secondary me-2" onClick={handleSaveDraft}>
            Save as Draft
          </Button>
          <Button type="button" disabled={activeIndex === 0} className="p-button p-button-secondary me-2" onClick={onPrevClick}>
            Previous Step
          </Button>
          <Button type="button" className="p-button p-button-secondary" disabled={loading} onClick={onNextClick}>
            {activeIndex && activeIndex === 2 ? 'Add Lead' : 'Next Step'}
          </Button>
        </div>
      )} */}
    </Sidebar>
  );
};

export default AddNewLead;
